import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError, Subject } from 'rxjs';
import { switchMap, tap, catchError, finalize } from 'rxjs/operators';
import { Router } from '@angular/router'; // Import Router
import { Inject, Injectable, Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';

import { DatePipe } from '@angular/common';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();
  private isRenewing = false;
  private validFor = 0;
  private refreshTokenTimeout: NodeJS.Timeout | null = null;
  private sessionTimeout: NodeJS.Timeout | null = null;
  private sessionExpiredSubject = new Subject<void>();
  public sessionExpired$ = this.sessionExpiredSubject.asObservable();

  constructor(private http: HttpClient, private router: Router, @Inject('BASE_URL') private originUrl: string) {
    this.loadInitialToken();
  }

  private loadInitialToken(): void {
    const storedToken = sessionStorage.getItem('auth_token');
    const storedExpiration = sessionStorage.getItem('tokenExpiration'); // Also load expiration
    if (storedToken && storedExpiration && !this.isTokenExpired("loadInitialToken")) {
      this.tokenSubject.next(storedToken);
    }
    else {
      this.tokenSubject.next(null); //clear the token.
    }
  }

  get currentToken(): string | null {
    this.tokenSubject.next(sessionStorage.getItem('auth_token'));
    return this.tokenSubject.value;
  }

  getToken(): Observable<string | null> {
    if (this.currentToken && !this.isTokenExpired("getToken")) {
      return of(this.currentToken);
    }
    return this.checkAndRenewToken().pipe(
      switchMap(token => { // Use switchMap to ensure we get the latest token
        return of(token);
      })
    );
  }



  private checkAndRenewToken(): Observable<string | null> {
    if (this.isTokenExpired("checkAndRenewToken")) {
      return this.renewToken().pipe(
        catchError((error: HttpErrorResponse) => {
          this.sessionExpiredSubject.next();
          this.clearAuthData();  // Call clearAuthData here
          return throwError(error);
        })
      );
    } else {
      return of(this.currentToken);
    }
  }


  isTokenExpired(source): boolean {
    //console.log("AuthService: isTokenExpired");
    let tokenExpiration: string | null;
    //console.log("isTokenExpired source         : " + source);
    const storedExpiration = sessionStorage.getItem('tokenExpiration');
    if (storedExpiration === "undefined" || storedExpiration === null || !storedExpiration) {
      //console.log("AuthService: isTokenExpired : storedExpiration not found");
      tokenExpiration = null;
    } else {
      //console.log("AuthService: isTokenExpired : storedExpiration found");
      tokenExpiration = storedExpiration;
    }
    const auth_token = sessionStorage.getItem('auth_token');
    let currentDateTime = new Date();

    if (!tokenExpiration) {
      //console.log("AuthService: isTokenExpired : token not found");
      return true;
    }
    const expirationDate = new Date(sessionStorage.getItem('tokenExpiration'));
    const now = new Date();
    //console.log("AuthService: isTokenExpired : " + (currentDateTime.toJSON().slice(0, 19) >= tokenExpiration.slice(0, 19)));
    ////console.log("isTokenExpired auth_token      : " + auth_token);
    //console.log("isTokenExpired tokenExpiration         : " + tokenExpiration.slice(0, 19));
    //console.log("isTokenExpired currentDateTime.toJSON  : " + (currentDateTime.toJSON().slice(0, 19)));
    return now.getTime() >= expirationDate.getTime();
  }



  renewToken1(): Observable<string> {
    if (this.isRenewing) {
      return this.token$;
    }

    this.isRenewing = true;
    //console.log("AuthService: renewToken : renewing ------------------------ ");
    // Get data from localStorage
    const domainId = JSON.parse(sessionStorage.getItem('currentDomain') || 'null');
    const userName = JSON.parse(sessionStorage.getItem('currentUser') || 'null');
    const dataRole = JSON.parse(sessionStorage.getItem('currentDataRole') || 'null');
    const processRole = JSON.parse(sessionStorage.getItem('currentProcessRole') || 'null');

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' }); // Set the content type here

    const body = JSON.stringify({ domainId, userName, dataRole, processRole });

    return this.http.post<any>("api/BcpJwtAuth/renewToken", body, { headers }).pipe(
      tap(response => {
        const res = JSON.parse(response);
        const newToken = res.auth_token;
        const validFor = res.validFor;
        const tokenExpiration = res.tokenExpiration;
        const longTokenExpiration = res.longTokenExpiration;


        this.tokenSubject.next(newToken); // Update the BehaviorSubject

        // Store updated token and other data in localStorage
        sessionStorage.setItem('currentDomain', JSON.stringify(domainId));
        sessionStorage.setItem('currentUser', JSON.stringify(userName));
        sessionStorage.setItem('currentProcessRole', JSON.stringify(res.processRole));
        sessionStorage.setItem('currentDataRole', JSON.stringify(res.dataRole));
        sessionStorage.setItem('auth_token', newToken);
        sessionStorage.setItem('auth_token_long', res.auth_token_long);
        sessionStorage.setItem('validFor', JSON.stringify(validFor));
        sessionStorage.setItem('tokenExpiration', tokenExpiration);
        sessionStorage.setItem('longTokenExpiration', longTokenExpiration);
        sessionStorage.setItem('sessionExpiration', tokenExpiration);

        this.validFor = validFor;
        this.setRefreshTokenTimeout(validFor);

        let currentDateTime = new Date();
        console.error("AuthService: Token renewed successfully at : " + new Date(new Date().getTime() + (8 * 60 * 60 * 1000)).toJSON().slice(0, 19));
        //console.error("New Token Expires at: ", tokenExpiration, " ");
        //this.showTimeoutInfo(validFor,"RenewToken");
        
      }),
      catchError((error: HttpErrorResponse) => {
        console.error("AuthService: Error renewing token:", error);
        this.clearAuthData();
        this.tokenSubject.next(null);
        return throwError(error);
      }),
      finalize(() => {
        this.isRenewing = false;
      }),
      switchMap(() => this.token$) // Ensure we return the token
    );
  }
  renewToken(): Observable<string> {
    if (this.isRenewing) {
      return this.token$;
    }

    this.isRenewing = true;

    // Set the proper content type header
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // =========================================================================
    // SECURITY FIX: Send an empty body payload. 
    // The server now safely extracts Domain, User, and Roles from the active token header!
    // =========================================================================
    const body = {};
    return this.http.post<any>("api/BcpJwtAuth/renewToken", body, { headers }).pipe(
      tap(response => {
        // Handle either raw text string responses or already-parsed JSON objects
        const res = typeof response === 'string' ? JSON.parse(response) : response;

        const newToken = res.auth_token;
        const validFor = res.validFor;
        const tokenExpiration = res.tokenExpiration;
        const longTokenExpiration = res.longTokenExpiration;

        this.tokenSubject.next(newToken); // Update the BehaviorSubject tracking user state

        // Store updated session data securely derived from the fresh token
        sessionStorage.setItem('currentDomain', JSON.stringify(res.domainId));
        sessionStorage.setItem('currentUser', JSON.stringify(res.loginId));
        sessionStorage.setItem('currentProcessRole', JSON.stringify(res.processRole));
        sessionStorage.setItem('currentDataRole', JSON.stringify(res.dataRole));
        sessionStorage.setItem('auth_token', newToken);
        sessionStorage.setItem('auth_token_long', res.auth_token_long);
        sessionStorage.setItem('validFor', JSON.stringify(validFor));
        sessionStorage.setItem('tokenExpiration', tokenExpiration);
        sessionStorage.setItem('longTokenExpiration', longTokenExpiration);
        sessionStorage.setItem('sessionExpiration', tokenExpiration);

        this.validFor = validFor;
        this.setRefreshTokenTimeout(validFor);

        console.error("AuthService: Token renewed successfully at : " + new Date(new Date().getTime() + (8 * 60 * 60 * 1000)).toJSON().slice(0, 19));
      }),
      catchError((error: HttpErrorResponse) => {
        console.error("AuthService: Error renewing token:", error);
        this.clearAuthData();
        this.tokenSubject.next(null);
        return throwError(error);
      }),
      finalize(() => {
        this.isRenewing = false;
      }),
      switchMap(() => this.token$) // Ensure we return the token stream context
    );
  }

  clearAuthData(): void {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('tokenExpiration');
    this.tokenSubject.next(null);
    this.clearRefreshTokenTimeout();
    this.clearSessionTimeout();
  }

  // ********** Refreshtoken Timeout
  public setRefreshTokenTimeout(seconds: number): void {
    //console.error("AuthService: setRefreshTokenTimeout : " + new Date().toJSON().slice(0, 19));

    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }

    this.refreshTokenTimeout = setTimeout(() => {
      this.renewToken().subscribe(
        () => {
          //console.error("AuthService: setRefreshTokenTimeout Execute : " + new Date().toJSON().slice(0, 19));
        },
        (error: HttpErrorResponse) => {
          console.error("AuthService: Token refresh failed by timeout:", error);
          this.sessionExpiredSubject.next();
          this.clearAuthData(); // And also here
        }
      );
    }, (seconds - 45) * 1000);
  }

  clearRefreshTokenTimeout(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
  }


  // ********** Session Timeout
  public setSessionTimeout(seconds: number = this.validFor): void {
    //console.error("AuthService: setSessionTimeout : " + new Date().toJSON().slice(0, 19));
    //this.showTimeoutInfo(seconds, "setSessionTimeout");
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
    }

    const sessionExpiration = new Date(new Date().getTime() + (seconds-60) * 1000 + 8*60*60*1000);
    sessionStorage.setItem('sessionExpiration', sessionExpiration.toJSON().slice(0, 19));
    //console.log("AuthService: setSessionTimeout : " + sessionExpiration.toJSON().slice(0, 19));

    this.sessionTimeout = setTimeout(() => {
      //this.showTimeoutInfo(seconds,"sessoinTimeoutExecute");
      console.error("AuthService: setSessionTimeout > session ended at : " + new Date(new Date().getTime() + (8 * 60 * 60 * 1000)).toJSON().slice(0, 19));
        this.sessionExpiredSubject.next();
        this.logout();
    }, (seconds - 60) * 1000);
  }

  clearSessionTimeout(): void {
    //console.log("AuthService: clearSessionTimeout");
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
  }

  login(token: string, expiresInSeconds: number): void {
    const expirationDate = new Date(new Date().getTime() + expiresInSeconds * 1000);
    sessionStorage.setItem('auth_token', token);
    sessionStorage.setItem('tokenExpiration', expirationDate.toISOString());
    sessionStorage.setItem('sessionExpiration', expirationDate.toISOString());
    this.tokenSubject.next(token);
    //console.log("AuthService: login : " + expiresInSeconds.toString());
    this.setRefreshTokenTimeout(expiresInSeconds);
    this.setSessionTimeout(expiresInSeconds);
    this.validFor = expiresInSeconds;
  }

  logout(): void {
    this.clearAuthData(); // And here
    this.router.navigate(['/login']); // Redirect to the login page

  }

  showTimeoutInfo(seconds: number, source: string) {
    const storedToken = sessionStorage.getItem('auth_token');
    const storedExpiration = sessionStorage.getItem('tokenExpiration'); // Also load expiration
    let currentDateTime = new Date();

    console.error(" ");
    console.error(" =====  " + source + " ===== ");
    console.error("showTimeoutInfo: Now                    : " + currentDateTime.toJSON().slice(0, 19));
    console.error("showTimeoutInfo: tokenExpiration        : " + storedExpiration);
    console.error("showTimeoutInfo: sessionExpiration      : " + sessionStorage.getItem('sessionExpiration'));

    console.error("=============================================");

  }
}

